import {Component, Input, ChangeDetectorRef, OnInit} from '@angular/core';
import * as _ from 'lodash';

import { attributesSegmentData } from '../../../segment-data/attributes-segment-data';
import { getItemValidators, initValidators, hasServerError, getFieldByName } from 'utils/utils';

import { BaseScorecardComponent } from '../base/_base-scorecard.component';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { ScorecardItemComponent } from 'shared/scorecard/scorecard-item/scorecard-item.component';

import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';


@Component({
  moduleId: module.id,
  selector: 'attributes-scorecard',
  templateUrl: 'attributes-scorecard.component.html',
  styleUrls: ['attributes-scorecard.component.scss'],
})
export class AttributesScorecardComponent extends BaseScorecardComponent implements OnInit {
  segmentTitle: string = 'Attributes';
  segmentData: any = attributesSegmentData;
  pathToScorecard: any = 'attributes';

  private scoreFields: Array<string> = ['score', 'populated', 'valid'];

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get segmentScores(): any {
    return _.get(this.scoreData, 'segments.attributes.tallies');
  }

  get itemDetailAttributes(): Array<any> {
    return _.get(this.itemDetail, 'attributes');
  }

  get isMissingAttribute(): boolean {
    return _.has(this.scoreData, 'segments.attributes.non-standard.missing');
  }

  get missingScore(): any {
    return _.get(this.scoreData, 'segments.attributes.non-standard.missing');
  }

  get attributesSize(): number {
    return _.size(this.itemDetailAttributes);
  }

  /*******************************************
  * COMPONENT SETTERS
  *******************************************/

  set itemDetailAttributes(data: Array<any>) {
    _.set(this.itemDetail, 'attributes', data);
  }

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
  ) {
    super(ref, scorecardConnectorService);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();
    if (this.isMissingAttribute && !_.size(this.segmentScores)) {
      // at least one score must always be when attribute missing
      this.missingScore.valid = 0;
      this.missingScore.populated = 0;
      this.addNewScore(false);
    }
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onScorecardAttributeExpand(item: SegmentFieldItem, index: number|string) {
    this.scorecardConnectorService.scoreDetailsExpanded.emit({item, index});
  }

  onScorecardAttributeHover(item: SegmentFieldItem, index: number|string) {
    this.scorecardConnectorService.scorecardItemHovered.emit({item, index});
  }

  onScorecardAttributeBlur(item: SegmentFieldItem, index: number|string) {
    this.scorecardConnectorService.scorecardItemBlured.emit({item, index});
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  isItemMissing(item: any): boolean {
    return _.get(item, 'isMissing');
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  /*
    * Replaced parent
    * $event: {status: string, item: SegmentFieldItem, index: number, valueIndex?: number|null}
   */
  protected getItemFieldScoreFromEvent($event: any) {
    if (_.isNil($event.valueIndex)) {
      return this.getScoreItem($event.item, $event.index);
    } else {
      return this.getScoreIndexItem($event.item, $event.index, $event.valueIndex);
    }
  }

  /*
    * * Replaced parent
   */
  protected handleControlStatusChange($event?: any): void {
    // don't handle padb not valid for part type
    if (this.getAttributeData($event.index).invalidPadb) { return; }
    super.handleControlStatusChange($event);
  }

  /*
    * Replaced parent
    * $event: {item: SegmentFieldItem, index: number, valueIndex?: number}
   */
  protected handleControlFocus($event: any) {
    this.handleNestedControlFocus($event);
  }

  protected findScorecardItemViaData(data: any): ScorecardItemComponent {
    return this.scorecardItems.toArray()[data.index];
  }

  protected collapseChildScoresViaData(scorecardItem: ScorecardItemComponent, data: any) {
    if (scorecardItem instanceof ScorecardItemComponent) {
      this.collapseExpandedChildScores(scorecardItem.scorecardChildItems, data.item.field_map);
    }
  }

  protected getChildScorecardItemViaData(scorecardItem: ScorecardItemComponent, data: any): ScorecardItemComponent {
    if (!scorecardItem) {
      return scorecardItem;
    }
    return this.getChildScorecardItemByName(scorecardItem, data.item.field_map);
  }

  /*
    * $event: {isPadb: boolean, index?: number}
   */
  protected handleControlAdd($event: any) {
    if (!_.isNil($event.index)) {
      this.addNewScoreToIndex($event.index);
    } else {
      this.addNewScore($event.isPadb);
    }
  }

  /*
    * $event: {isPadb: boolean, index?: number, valueIndex?: number}
   */
  protected handleControlRemove($event: any) {
    if (!_.isNil($event.valueIndex)) {
      const score: any = _.get(_.nth(this.segmentScores, $event.index), 'value');
      _.pullAt(score, [$event.valueIndex]);
      this.ref.markForCheck();
    }
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  /*
    * Checks attribute's high level validity
   */
  private isAttributeItemValid(index: number): boolean {
    return _.every(_.nth(this.segmentScores, index), (scoreItem: any) => {
      if (_.isArray(scoreItem)) {
        return _.every(scoreItem, (score: any) => {
          return score.valid === 1;
        });
      } else {
        return scoreItem.valid === 1;
      }
    });
  }

  private getAttributeData(index: number|string): any {
    return _.get(this.itemDetail, `attributes[${index}]`);
  }

  private getScoreItem(item: SegmentFieldItem, index: number|string): any {
    return _.get(this.segmentScores, `${index}.${item.item_name}`);
  }

  private getScoreIndexItem(item: SegmentFieldItem, index: number|string, propertyIndex: number): any {
    return _.get(this.segmentScores, `${index}.${item.item_name}[${propertyIndex}]`);
  }

  private getItemValidationMessage(item: any, attribute: any, index: number, valueIndex?: number): string {
    const score = this.getItemFieldScoreFromEvent({item, index, valueIndex});

    if (this.isPadb(attribute)) {
      // check if PAdb ID is not valid (SPD2-3188, SPD2-3305)
      if (this.hasPadbError(attribute)) {
        // error is being added in attributes.component while update attribute form
        return attribute.padbError;
      }
      // padb meta_data rules
      if (!_.get(attribute, 'meta_data')) {
        return this.defaultInvalidText;
      }
      const maxLength = _.toNumber(_.get(attribute, 'meta_data.rules.max_length', 2000));
      const decimalPoints = _.toNumber(_.get(attribute, 'meta_data.rules.decimal_points', 0));
      return `Attribute value cannot be longer than ${maxLength} characters` +
        (decimalPoints ? ` including ${decimalPoints} decimal places` : '') +
        (this.isInputNumber(attribute) ? ' and cannot have Alphabetical characters' : '');
    } else {
      if (score.valid) { return ''; }
      return this.getValidationMessage(item, score);
    }
  }

  /*
    * Checks every score in the list
   */
  private isListScoreValid(item: SegmentFieldItem, attribute: any, attrIndex: number|string): boolean {
    if (this.hasPadbError(attribute)) { return false; }
    return _.every(this.getScoreItem(item, attrIndex), (score: any) => {
      return _.get(score, 'valid', 1) === 1;
    });
  }

  private isSingleScoreValid(item: SegmentFieldItem, attribute: any, index: number): number {
    if (this.hasPadbError(attribute)) { return 0; }
    return _.get(this.getScoreItem(item, index), 'valid');
  }

  private isSingleScorePopulated(item: SegmentFieldItem, attribute: any, index: number): number {
    // if user hasn't added value for padb attribute it will be shown as single score instead of list
    // return populated 1 for displaying invalid icon
    if (attribute.is_value_list) { return 1; }
    return _.get(this.getScoreItem(item, index), 'populated');
  }

  private isListItemValid(item: SegmentFieldItem, attribute: any, attrIndex: number, valueIndex: number) {
    if (this.hasPadbError(attribute)) { return 0; }
    return _.get(this.getScoreIndexItem(item, attrIndex, valueIndex), 'valid');
  }

  private getErrorPath(attributeIndex: number, name: string, valueIndex?: number): string {
    const controlPath = !_.isNil(valueIndex) ? `.${valueIndex}` : '';
    return `attributes.${attributeIndex}.${name}${controlPath}`;
  }

  private isInputNumber(item: any): boolean {
    if (item.is_padb === 'Y') {
      return _.includes(['number', 'decimal'], _.get(item, 'meta_data.data_type'));
    } else {
      return false;
    }
  }

  private isPadb(item: any): boolean {
    return _.get(item, 'is_padb') === 'Y';
  }

  private addNewScore(isPadb: boolean): void {
    const newScore: any = {
      name: this.getEmptyScoreItem(this.getScorecardFieldValidationRule('.name')),
      uom: this.getEmptyScoreItem(this.getScorecardFieldValidationRule('.uom')),
      value: this.getEmptyScoreItem(this.getScorecardFieldValidationRule('.value')),
    };
    _.each(this.scoreFields, (prop: string) => {
      if (isPadb) {
        _.each(newScore, (scoreItem: any, key: string) => {
          _.set(scoreItem, prop, (key === 'uom' || key === 'name' ? 1 : 0));
        });
      } else {
        _.each(newScore, (scoreItem: any, key: string) => {
          _.set(scoreItem, prop, (key === 'uom' ? 1 : 0));
        });
      }
    });
    if (isPadb) {
      newScore.value = [_.cloneDeep(newScore.value)];
    }
    if (this.segmentScores) {
      this.segmentScores.push(newScore);
      this.ref.markForCheck();
    }
  }

  private addNewScoreToIndex(index: number): void {
    const scoreItem: any = _.nth(this.segmentScores, index);
    const newValueScore: any = this.getEmptyScoreItem(this.getScorecardFieldValidationRule('.value'));
    if (!scoreItem.value) {
      _.set(scoreItem, 'value', []);
    }
    _.each(this.scoreFields, (prop: string) => {
      newValueScore[prop] = 0;
    });
    if (!this.isItemArray(scoreItem.value)) {
      scoreItem.value = scoreItem.value ? [_.cloneDeep(scoreItem.value)] : [];
    }
    scoreItem.value.push(newValueScore);
    this.ref.markForCheck();
  }

  private hasMissingAttrName(): boolean {
    return _.size(_.get(this.itemDetailAttributes, '[0].name', '')) > 0;
  }

  private hasMissingAttrValue(): boolean {
    return _.get(this.segmentScores, `[0].value.populated`) === 1;
  }

  private hasPadbError(attribute: any): boolean {
    return _.get(attribute, 'padbError');
  }
}
